(function () {
  'use strict';
  if (window.WV500Series) { window.WV500Series.init(); return; }
  const engine = window.WV500SeriesState;
  const instances = new WeakMap();
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
      if (response.status === 429) {
        const error = new Error('Store request limited');
        error.code = 'RATE_LIMITED';
        throw error;
      }
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'Request failed');
        error.status = response.status;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  class Configurator {
    constructor(root) {
      this.root = root;
      this.data = JSON.parse(root.querySelector('[data-wvc-data]').textContent);
      this.en = this.data.locale.split('-')[0] === 'en';
      if (!engine.validate(this.data)) throw new Error('Invalid configuration mapping');
      this.money = new Intl.NumberFormat(this.data.locale, { style: 'currency', currency: this.data.currency });
      this.controllers = new AbortController();
      this.busy = false;
      this.cartIssue = Boolean(sessionStorage.getItem('wv-configurator-pending'));
      this.mediaVersion = 0;
      this.groups = this.buildGroups();
      const initial = engine.initial(this.data, new URLSearchParams(location.search));
      this.selection = initial.selection;
      this.needsAcknowledgment = initial.reset;
      if (!this.data.configurations.length) throw new Error('No configurations');
      this.createGroups();
      this.bind();
      this.render();
      if (initial.migrated && !initial.reset) this.writeURL();
      this.status(initial.reset ? this.text('Der geteilte Link ist nicht mehr vollständig verfügbar. Bitte prüfe die wiederhergestellte Auswahl.', 'Some shared options are no longer available. Please check the restored selection.') : '');
      if (this.cartIssue) {
        this.status(this.text('Eine frühere Übertragung konnte nicht bestätigt werden. Bitte prüfe zuerst den Warenkorb.', 'A previous transfer could not be confirmed. Please check your cart first.'));
        this.find('cart-link').hidden = false;
      }
      this.observer = new IntersectionObserver(entries => { this.find('sticky').hidden = entries[0].isIntersecting; }, { threshold: 0 });
      this.observer.observe(this.root.querySelector('.wvc__summary'));
      this.stickySizeObserver = new ResizeObserver(() => {
        const height = this.find('sticky').getBoundingClientRect().height;
        if (height > 0) document.body.style.setProperty('--wvc-sticky-height', height + 'px');
      });
      this.stickySizeObserver.observe(this.find('sticky'));
      if (this.cartIssue) this.reconcileCart();
    }
    text(german, english) { return this.en ? english : german; }
    find(name) { return this.root.querySelector('[data-wvc-' + name + ']'); }
    price(value) { return this.money.format(value / 100); }
    productName(product) {
      const configuration = this.data.configurations.find(item => item.items[0]?.product === product.handle);
      if (configuration) return configuration.modelLabel;
      return product.title.split(' – ')[0];
    }
    itemName(item) {
      return this.productName(item.product) + (item.variant.title && item.variant.title !== 'Default Title' ? ' · ' + item.variant.title : '');
    }
    status(message) { this.find('status').textContent = message; }
    productImage(product) { return product?.featured_image?.src || product?.featured_image || product?.images?.[0]?.src || product?.images?.[0] || ''; }
    imageSource(source, size) {
      if (!source) return '';
      const url = new URL(source, location.href);
      if (url.hostname === 'cdn.shopify.com') url.searchParams.set('width', String(size));
      return url.href;
    }
    image(url, alt, size = 240) {
      const image = element('img');
      image.src = this.imageSource(url, size);
      image.alt = alt;
      image.width = size;
      image.height = size;
      image.loading = 'lazy';
      image.draggable = false;
      return image;
    }
    frameFor(slot) {
      if (slot === 'panels' && this.result.configuration?.panelFrame) return this.result.configuration.panelFrame;
      return this.data.sceneFrames?.[slot === 'main' ? this.selection.model : slot] || [100, 100, 0, 0, 100, 100];
    }
    frameSprite(sprite, frame) {
      const [width, height, left, top, cropWidth, cropHeight] = frame;
      const image = sprite.querySelector('img');
      sprite.style.aspectRatio = cropWidth + ' / ' + cropHeight;
      sprite.style.setProperty('--sprite-ratio', cropWidth / cropHeight);
      image.style.width = width / cropWidth * 100 + '%';
      image.style.height = height / cropHeight * 100 + '%';
      image.style.left = -left / cropWidth * 100 + '%';
      image.style.top = -top / cropHeight * 100 + '%';
    }
    buildGroups() {
      const labels = {
        model: [this.text('System', 'System'), this.text('Welches System passt?', 'Which system should I choose?'), this.text('Wähle das Hauptgerät. Die weiteren Optionen zeigen die dafür hinterlegten Zusammenstellungen. Eine Erweiterungsbatterie erhöht die Kapazität, nicht die Ausgangsleistung.', 'Choose the main unit. The following options show the configurations available for it. An expansion battery adds capacity, not output power.')],
        capacity: [this.text('Zusatzbatterien', 'Extra batteries'), this.text('Wie viele B500 möchtest Du ergänzen?', 'How many B500 batteries would you like to add?'), this.text('Wähle 0 bis 5 zusätzliche B500. Der Hauptspeicher ist bereits enthalten und zählt nicht als Zusatzbatterie. Die Gesamtangabe umfasst Hauptspeicher und B500 und bezeichnet die nominale Speichergröße, nicht die nutzbare Kapazität. Jede gewählte B500 wird als eigener Artikel mit der gewählten Anzahl hinzugefügt. Mehr B500 erhöhen die Kapazität, nicht die Ausgangsleistung.', 'Choose 0 to 5 additional B500 batteries. The main storage unit is already included and is not counted as an extra battery. The total includes the main unit and B500 batteries and describes nominal storage size, not usable capacity. Each selected B500 is added as a separate item with the selected quantity. More B500 batteries add capacity, not output power.')],
        panels: [this.text('Solarmodule', 'Solar panels'), this.text('Mit oder ohne Solarmodule?', 'With or without solar panels?'), this.text('Solarmodule werden als Einzelartikel ergänzt, nicht als fertiges Speicher-Set. Prüfe vor dem Anschluss Spannung, Strom und Anschlussplanung. Wir helfen bei der Prüfung.', 'Solar panels are added as individual products, not as a storage bundle. Check voltage, current and connection planning before connecting them. Ask us to check.')],
        mount: [this.text('Halterung', 'Mounting'), this.text('Welche Halterung passt?', 'Which mounting system fits?'), this.text('Halterung und Befestigung richten sich nach Modulen und Montageort. Ohne zusätzlich gewählte Halterung wird hier kein weiterer Artikel hinzugefügt. Maßgeblich ist der Lieferumfang der einzelnen Artikel.', 'Mounting depends on the panels and installation location. Without an additional mounting choice, no extra item is added here. Check each selected item’s scope of delivery.')],
        meter: ['Smart Meter', this.text('Welcher Zähler passt?', 'Which meter is compatible?'), this.text('SunEnergyXT nennt EcoTracker IR als unterstütztes Energiemessgerät für die 500er-Serie. Prüfe vor dem Kauf, ob Dein Haushaltsstromzähler mit EcoTracker IR kompatibel ist und die Voraussetzungen des Herstellers erfüllt sind. Prüfe auch den Lieferumfang der gewählten Einzelartikel.', 'SunEnergyXT lists EcoTracker IR as a supported energy meter for the 500 series. Before buying, check that your household electricity meter is compatible with EcoTracker IR and that the manufacturer’s requirements are met. Also check the selected individual products’ included items.')],
        ac_cable: [this.text('AC-Verlängerung', 'AC extension'), this.text('Welche Kabellänge brauche ich?', 'Which cable length do I need?'), this.text('Prüfe Anschlussart und benötigte Länge am Aufstellort. Bereits enthaltene Kabel werden nicht nochmals berechnet. Nicht freigegebene Verlängerungen bieten wir hier nicht zur Auswahl an.', 'Check the connector and required length at the installation location. Included cables are not charged again. Extensions without verified compatibility are not offered here.')],
        dc_cable: [this.text('DC-Verlängerung', 'DC extension'), this.text('Welche Kabel passen zu den Modulen?', 'Which cables fit the panels?'), this.text('Die passende DC-Verbindung hängt von Modulen, Anschluss und Leitungsweg ab. Die Länge allein bestätigt keine Kompatibilität. Teile uns die geplante Aufstellung mit.', 'The correct DC connection depends on the panels, connectors and cable route. Length alone does not confirm compatibility. Tell us about the planned installation.')]
      };
      return [...engine.keys, ...this.data.accessories.map(group => group.id)].map(id => {
        const configurations = [...new Map(this.data.configurations.map(item => [item[id], item])).values()];
        const accessory = this.data.accessories.find(group => group.id === id);
        const options = accessory ? accessory.options.map(option => ({ ...option, label: option.id === 'none' ? this.text('Ohne Zusatz', 'No extra') : option.label.replace(/\s*[–−-]\s*Kompatibilität prüfen/g, '').replace('Halterung · 4 Stück · ', this.text('4 Stück · ', '4 pieces · ')).replace('DC-Kabelpaar · ', '') })) : configurations.map(configuration => ({ id: configuration[id], label: id === 'model' ? configuration.modelLabel.replace('SunEnergyXT ', '') : id === 'capacity' ? (configuration.batteryCount ? configuration.batteryCount + ' × B500' : this.text('Ohne B500', 'No B500')) : configuration[id + 'Label'], models: [] }));
        if (id === 'capacity') options.sort((first, second) => Number(first.id) - Number(second.id));
        return { id, title: labels[id][0], help: labels[id][1], description: labels[id][2], options, accessory: Boolean(accessory) };
      }).filter(group => !group.accessory || group.options.some(option => option.id !== 'none'));
    }
    createGroups() {
      const container = this.find('groups');
      container.replaceChildren();
      for (const group of this.groups) {
        const fieldset = element('fieldset', 'wvc__group');
        fieldset.dataset.group = group.id;
        const legend = element('legend');
        legend.append(element('strong', '', group.title + (group.id === 'capacity' ? ': ' : ' ')), element('span', 'wvc__current'));
        const help = element('button', 'wvc__help', 'ⓘ');
        help.setAttribute('aria-label', group.help);
        help.title = group.help;
        help.type = 'button';
        help.dataset.help = group.id;
        const body = element('div', 'wvc__group-body');
        const preview = element('div', 'wvc__group-image');
        const options = element('div', 'wvc__options');
        for (const option of group.options) {
          const label = element('label', 'wvc__option');
          const input = element('input');
          input.type = 'radio';
          input.name = this.root.id + '-' + group.id;
          input.value = option.id;
          input.dataset.group = group.id;
          const content = element('span');
          content.append(element('strong', '', option.label), element('small', 'wvc__delta'));
          if (group.id === 'capacity') {
            const configuration = this.data.configurations.find(configuration => configuration.capacity === option.id);
            content.querySelector('strong').textContent = configuration.batteryCount + ' × B500';
            content.insertBefore(element('small', 'wvc__quantity', configuration.capacityLabel + this.text(' gesamt', ' total')), content.querySelector('.wvc__delta'));
            const saving = element('small', 'wvc__offer-saving');
            saving.hidden = true;
            content.append(saving);
          }
          label.append(input, content);
          options.append(label);
        }
        body.append(preview, options);
        fieldset.append(legend, help, body);
        const compatibilityNote = element('p', 'wvc__compatibility-note');
        compatibilityNote.id = this.root.id + '-' + group.id + '-compatibility';
        compatibilityNote.hidden = true;
        fieldset.append(compatibilityNote);
        if (group.id === 'panels') {
          const quantity = element('div', 'wvc__panel-quantity');
          const label = element('label', '', this.text('Anzahl der Module', 'Number of panels'));
          const input = element('input');
          input.type = 'number';
          input.min = '0';
          input.max = '20';
          input.step = '1';
          input.inputMode = 'numeric';
          input.id = this.root.id + '-panel-quantity';
          input.dataset.panelQuantity = '';
          label.htmlFor = input.id;
          const controls = element('div', 'wvc__stepper');
          for (const [step, symbol, name] of [[-1, '−', this.text('Ein Modul weniger', 'One fewer panel')], [1, '+', this.text('Ein Modul mehr', 'One more panel')]]) {
            const button = element('button', '', symbol);
            button.type = 'button';
            button.dataset.quantityStep = step;
            button.setAttribute('aria-label', name);
            controls.append(button);
            if (step === -1) controls.append(input);
          }
          const note = element('p', 'wvc__quantity-note');
          note.id = input.id + '-note';
          input.setAttribute('aria-describedby', note.id);
          quantity.append(label, controls, note);
          fieldset.append(quantity);
        }
        if (group.id === 'dc_cable') {
          const quantity = element('div', 'wvc__cable-quantity');
          const label = element('label', '', this.text('Anzahl der Kabelpaare', 'Number of cable pairs'));
          const input = element('input');
          input.type = 'number';
          input.min = '1';
          input.step = '1';
          input.inputMode = 'numeric';
          input.id = this.root.id + '-cable-quantity';
          input.dataset.cableQuantity = '';
          label.htmlFor = input.id;
          const controls = element('div', 'wvc__stepper');
          for (const [step, symbol, name] of [[-1, '−', this.text('Ein Kabelpaar weniger', 'One fewer cable pair')], [1, '+', this.text('Ein Kabelpaar mehr', 'One more cable pair')]]) {
            const button = element('button', '', symbol);
            button.type = 'button';
            button.dataset.cableStep = step;
            button.setAttribute('aria-label', name);
            controls.append(button);
            if (step === -1) controls.append(input);
          }
          const note = element('p', 'wvc__quantity-note');
          note.id = input.id + '-note';
          note.dataset.cableNote = '';
          input.setAttribute('aria-describedby', note.id);
          quantity.append(label, controls, note);
          fieldset.append(quantity);
        }
        if (group.id === 'model' || group.id === 'capacity' || group.id === 'panels') {
          fieldset.append(element('p', 'wvc__comparison-hint', group.id === 'model' ? this.text('Preisänderung für die gesamte Auswahl. Zubehör, das beim Modellwechsel entfällt, ist berücksichtigt.', 'Price change for the entire selection, including accessories removed when switching models.') : this.text('Preisänderung gegenüber Deiner aktuellen Auswahl.', 'Price difference from your current selection.')));
        }
        if (group.id === 'capacity') {
          const comparison = element('p', 'wvc__upgrade-comparison');
          comparison.dataset.wvcUpgradeComparison = '';
          comparison.hidden = true;
          fieldset.append(comparison);
        }
        container.append(fieldset);
        group.node = fieldset;
      }
    }
    bind() {
      const options = { signal: this.controllers.signal };
      document.addEventListener('submit', event => {
        const form = event.target;
        if (!form.matches('form') || !form.querySelector('input[name="form_type"][value="localization"]')) return;
        const returnTo = form.querySelector('input[name="return_to"]');
        if (!returnTo) return;
        this.writeURL();
        returnTo.value = location.pathname + location.search + location.hash;
      }, { ...options, capture: true });
      this.root.addEventListener('change', event => {
        if (event.target.matches('[data-cable-quantity]')) {
          this.changeCableQuantity(event.target.value);
          return;
        }
        if (event.target.matches('[data-panel-quantity]')) {
          this.changeQuantity(event.target.value);
          return;
        }
        const input = event.target.closest('input[data-group]');
        if (!input || this.busy) return;
        const next = engine.select(this.data, this.selection, input.dataset.group, input.value);
        this.selection = next.selection;
        this.render();
        this.writeURL();
        this.status(next.resets.length ? this.text('Für dieses System wurde die Auswahl angepasst: ', 'The following choices were adjusted for this system: ') + next.resets.map(id => this.groups.find(group => group.id === id)?.title || id).join(', ') : '');
      }, options);
      this.root.addEventListener('click', event => {
        const cableStep = event.target.closest('[data-cable-step]');
        if (cableStep) this.changeCableQuantity(Math.max(1, (engine.cableQuantity(this.selection) ?? 0) + Number(cableStep.dataset.cableStep)));
        const step = event.target.closest('[data-quantity-step]');
        if (step) {
          const count = this.selection.panelQuantity ?? this.result.configuration?.panelCount ?? 0;
          this.changeQuantity(Math.max(0, Math.min(20, Number(count) + Number(step.dataset.quantityStep))));
        }
        const help = event.target.closest('[data-help]');
        if (help) {
          const group = this.groups.find(item => item.id === help.dataset.help);
          const content = element('div');
          content.append(element('p', '', group.description));
          if (group.id === 'capacity') {
            const comparison = group.node.querySelector('.wvc__upgrade-comparison');
            if (comparison?.textContent) content.append(element('p', '', comparison.textContent));
          }
          this.dialog(group.help, content);
        }
        if (event.target.closest('[data-wvc-show-tax]')) {
          const note = this.find('tax');
          if (note) {
            note.open = true;
            note.scrollIntoView({ block: 'center', behavior: 'instant' });
            note.querySelector('summary').focus({ preventScroll: true });
          }
        }
        const shortcut = event.target.closest('[data-focus-group]');
        if (shortcut) {
          const group = this.groups.find(item => item.id === shortcut.dataset.focusGroup);
          if (group) {
            this.find('dialog').close();
            group.node.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
            const input = group.node.querySelector('input:checked:not(:disabled), input:not(:disabled)');
            input?.focus({ preventScroll: true });
          }
        }
        if (event.target.closest('[data-wvc-close]')) this.find('dialog').close();
        if (event.target.closest('[data-wvc-add]')) this.add();
        if (event.target.closest('[data-wvc-buy]')) this.add('checkout');
        if (event.target.closest('[data-wvc-remove-review]') && !this.busy) {
          this.selection = engine.withoutUnverified(this.data, this.selection);
          this.render();
          this.writeURL();
          this.status(this.text('Ungeprüfte Extras entfernt. Prüfe die aktualisierte Auswahl und den Preis, bevor Du bestellst.', 'Unverified extras removed. Check the updated selection and price before ordering.'));
        }
        if (event.target.closest('[data-wvc-share]')) this.share();
        if (event.target.closest('[data-wvc-previous]')) this.galleryStep(-1);
        if (event.target.closest('[data-wvc-next]')) this.galleryStep(1);
        if (event.target.closest('[data-wvc-review-selection]')) {
          const target = this.cartIssue ? this.find('cart-link') : this.needsAcknowledgment ? this.find('acknowledge') : this.find('purchase-guard');
          target.scrollIntoView({ block: 'center', behavior: 'auto' });
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
        if (event.target.closest('[data-wvc-recheck-cart]')) this.reconcileCart();
        if (event.target.closest('[data-wvc-acknowledge]') && !this.busy && !this.checkoutPending && !this.cartIssue) {
          this.needsAcknowledgment = false;
          this.writeURL();
          this.buttons();
          this.status(this.text('Auswahl bestätigt.', 'Selection confirmed.'));
        }
        if (event.target.closest('[data-wvc-zoom]')) {
          const content = this.find('composition').hidden ? this.image(this.find('hero').src, this.find('hero').alt, 1000) : this.find('composition').cloneNode(true);
          content.hidden = false;
          this.dialog(this.text('Deine Auswahl', 'Your selection'), content);
        }
      }, options);
      this.find('dialog').addEventListener('click', event => {
        if (event.target !== this.find('dialog')) return;
        const box = event.target.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) event.target.close();
      }, options);
      const stage = this.root.querySelector('.wvc__stage');
      stage.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.galleryStep(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      stage.addEventListener('pointerdown', event => { this.pointerStart = event.target.closest('button') ? null : { x: event.clientX, y: event.clientY }; }, options);
      stage.addEventListener('pointercancel', () => { this.pointerStart = null; }, options);
      stage.addEventListener('pointerup', event => {
        if (!this.pointerStart) return;
        const horizontal = event.clientX - this.pointerStart.x;
        const vertical = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(horizontal) > 50 && Math.abs(horizontal) > Math.abs(vertical) * 1.5) this.galleryStep(horizontal < 0 ? 1 : -1);
      }, options);
      window.addEventListener('popstate', () => {
        if (this.busy) return;
        const restored = engine.initial(this.data, new URLSearchParams(location.search));
        this.selection = restored.selection;
        this.needsAcknowledgment = restored.reset;
        this.render();
        if (restored.reset) this.status(this.text('Bitte bestätige die wiederhergestellte Auswahl.', 'Please confirm the restored selection.'));
      }, options);
    }
    changeCableQuantity(value) {
      if (this.busy) return;
      this.selection = engine.select(this.data, this.selection, 'dcQuantity', value).selection;
      this.render();
      this.writeURL();
      this.status('');
    }
    changeQuantity(value) {
      if (this.busy) return;
      this.selection = engine.select(this.data, this.selection, 'panelQuantity', value).selection;
      this.render();
      this.writeURL();
      this.status('');
    }
    mainProduct() {
      return this.result.items[0]?.product || this.data.products[this.result.configuration?.items?.[0]?.product];
    }
    render() {
      this.result = engine.resolve(this.data, this.selection);
      this.root.dataset.guaranteeItems = JSON.stringify(this.result.items.map(item => ({id:item.variant.id, quantity:item.quantity})));
      this.root.dispatchEvent(new CustomEvent('wv:guarantee-selection', {bubbles:true}));
      const quote = engine.offerPricing(this.data, this.result);
      this.root.dataset.model = this.selection.model;
      for (const node of this.root.querySelectorAll('[data-wvc-models]')) {
        node.hidden = !node.dataset.wvcModels.split(',').includes(this.selection.model);
      }
      const main = this.mainProduct();
      const quantityReview = this.result.reason?.startsWith('panel-quantity');
      const unverified = engine.unverifiedOptions(this.data, this.selection);
      for (const group of this.groups) {
        const unavailableGroup = group.id === 'panels' ? !engine.supportsPanels(this.data, this.selection.model) : group.accessory && !group.options.some(option => option.product && engine.compatible(option, this.selection.model));
        group.node.hidden = unavailableGroup && !['panels', 'mount', 'dc_cable'].includes(group.id);
        group.node.dataset.unavailable = String(Boolean(unavailableGroup));
        const compatibilityNote = group.node.querySelector('.wvc__compatibility-note');
        compatibilityNote.hidden = !unavailableGroup;
        compatibilityNote.textContent = group.id === 'panels' ? this.text('AC CORE: Die direkten PV-Eingänge sind in der Basisversion nicht freigeschaltet. Solarmodule lassen sich deshalb hier nicht hinzufügen.', 'AC CORE: Direct solar inputs are not enabled in the base version, so solar panels cannot be added here.') : this.text('Diese Auswahl gehört zur direkten PV-Konfiguration von 500 und 500 PRO. Zubehör für Deine bestehende PV-Anlage klären wir separat mit Dir.', 'This selection belongs to the direct-PV configuration of 500 and 500 PRO. Contact us separately about accessories for your existing solar installation.');
        const chosen = group.options.find(option => option.id === this.selection[group.id]);
        const hint = group.node.querySelector('.wvc__comparison-hint');
        if (hint) {
          const showOfferHint = group.id === 'capacity' && this.data.batteryOfferEnabled === true && this.data.currency === 'EUR';
          hint.hidden = group.id !== 'model' && !showOfferHint;
          if (showOfferHint) hint.textContent = this.text('Preisänderung gegenüber Deiner aktuellen Auswahl, inklusive B500-Mengenrabatt. Der endgültige Rabatt wird für den gesamten Warenkorb berechnet.', 'Price difference from your current selection, including the B500 quantity discount. The final discount is calculated for your entire cart.');
        }
        group.node.querySelector('.wvc__current').textContent = group.id === 'capacity' ? this.text('Gesamt: ', 'Total: ') + (this.result.configuration?.capacityLabel || '—') : chosen?.label || '—';
        for (const input of group.node.querySelectorAll('input[type=radio]')) {
          input.checked = input.value === this.selection[group.id] && !(group.id === 'panels' && quantityReview);
          const option = group.options.find(item => item.id === input.value);
          input.parentElement.hidden = !unavailableGroup && !group.accessory && group.id !== 'model' && !this.data.configurations.some(configuration => configuration.model === this.selection.model && configuration[group.id] === input.value);
          const next = engine.select(this.data, this.selection, group.id, input.value);
          const result = engine.resolve(this.data, next.selection);
          const compatible = engine.compatible(option, this.selection.model);
          const available = result.valid || result.reason === 'review' || result.reason === 'cable-quantity-invalid' || result.reason?.startsWith('panel-quantity');
          input.disabled = this.busy || unavailableGroup || !compatible || (!available && !input.checked && group.accessory && input.value !== 'none');
          if (unavailableGroup) input.setAttribute('aria-describedby', compatibilityNote.id);
          else input.removeAttribute('aria-describedby');
          if (input.parentElement.hidden || group.node.hidden) input.disabled = true;
          const delta = input.parentElement.querySelector('.wvc__delta');
          const optionQuote = engine.offerPricing(this.data, result);
          const difference = optionQuote.total === null || quote.total === null ? null : optionQuote.total - quote.total;
          const saving = input.parentElement.querySelector('.wvc__offer-saving');
          if (saving) {
            saving.hidden = optionQuote.discount === 0;
            saving.textContent = optionQuote.discount ? this.price(optionQuote.discount) + this.text(' Rabatt', ' discount') : '';
          }
          delta.textContent = unavailableGroup ? input.value === 'none' ? '' : this.text('Nicht auswählbar', 'Not selectable') : !compatible ? this.text('Nicht kompatibel', 'Not compatible') : !available && !input.checked ? this.text('Nicht verfügbar', 'Unavailable') : (input.checked && ['capacity', 'panels'].includes(group.id) ? this.text('Ausgewählt', 'Selected') : input.checked || difference === null || difference === 0 ? '' : (difference > 0 ? '+' : '−') + this.price(Math.abs(difference))) + (option.verified === false ? option.independentPurchase === true ? this.text(' · Separater Artikel', ' · Separate item') : this.text(' · Prüfung nötig', ' · Review required') : '');
        }
        const preview = group.node.querySelector('.wvc__group-image');
        let optionProduct = chosen?.product ? this.data.products[chosen.product] : main;
        if (group.id === 'model' || group.id === 'capacity') {
          const device = this.data.configurations.find(item => item.model === this.selection.model && item.capacity === this.selection.capacity && item.panels === 'none');
          const entry = group.id === 'capacity' && device?.items.length > 1 ? device.items[1] : device?.items[0];
          if (entry) optionProduct = this.data.products[entry.product];
        }
        const selectedVariant = chosen?.product ? engine.variant(optionProduct, chosen.variantId) : null;
        if (group.id === 'dc_cable') {
          const count = engine.cableQuantity(this.selection);
          const input = group.node.querySelector('[data-cable-quantity]');
          const disabled = this.busy || unavailableGroup || !chosen?.product;
          input.value = count ?? '';
          input.disabled = disabled;
          input.setAttribute('aria-invalid', String(!disabled && count === null));
          group.node.querySelector('[data-cable-step="-1"]').disabled = disabled || count === 1;
          group.node.querySelector('[data-cable-step="1"]').disabled = disabled;
          const note = group.node.querySelector('[data-cable-note]');
          note.textContent = unavailableGroup ? compatibilityNote.textContent : !chosen?.product ? this.text('Wähle zuerst eine Kabellänge.', 'Choose a cable length first.') : count === null ? this.text('Bitte gib eine ganze Anzahl ab 1 ein. Ein Paar enthält zwei Kabel.', 'Enter a whole number of 1 or more. One pair contains two cables.') : this.text('Ein Paar enthält zwei Kabel. Preis je Paar: ', 'One pair contains two cables. Price per pair: ') + (selectedVariant ? this.price(selectedVariant.price) : '—') + this.text('. Die Preisunterschiede oben gelten für die gewählte Anzahl.', '. The price differences above apply to your selected quantity.');
          if (chosen?.product && count !== null) group.node.querySelector('.wvc__current').textContent = chosen.label + ' · ' + count + this.text(count === 1 ? ' Paar' : ' Paare', count === 1 ? ' pair' : ' pairs');
        }
        const scene = engine.scene(this.data, this.selection);
        const layer = scene[group.id === 'model' ? 'main' : group.id === 'capacity' ? (scene.battery ? 'battery' : 'main') : group.id];
        const source = (group.accessory && !chosen?.product) || (group.id === 'panels' && this.selection.panels === 'none') ? '' : layer ? layer.source : selectedVariant?.featured_image?.src || this.productImage(optionProduct);
        if (source) {
          const sprite = element('div', 'wvc__sprite');
          sprite.append(this.image(source, chosen?.label || group.title));
          this.frameSprite(sprite, this.frameFor(group.id === 'model' ? 'main' : group.id === 'capacity' ? (scene.battery ? 'battery' : 'main') : group.id));
          preview.replaceChildren(sprite);
        } else preview.replaceChildren(element('span', 'wvc__no-extra', '—'));
        if (group.id === 'panels') {
          const count = this.selection.panelQuantity ?? this.result.configuration?.panelCount ?? 0;
          const input = group.node.querySelector('[data-panel-quantity]');
          input.value = count < 0 ? '' : count;
          input.disabled = this.busy || unavailableGroup;
          input.setAttribute('aria-invalid', String(count < 0));
          group.node.querySelector('[data-quantity-step="-1"]').disabled = this.busy || unavailableGroup || count === 0;
          group.node.querySelector('[data-quantity-step="1"]').disabled = this.busy || unavailableGroup || count >= 20;
          group.node.querySelector('.wvc__current').textContent = count < 0 ? this.text('Anzahl prüfen', 'Check quantity') : count + this.text(' Module', ' panels');
          const note = group.node.querySelector('.wvc__quantity-note');
          note.hidden = !quantityReview && this.result.reason !== 'panel-unavailable';
          note.textContent = this.result.reason === 'panel-unavailable' ? this.text('Dieses Solarmodul ist derzeit nicht einzeln online bestellbar. Es wird kein Komplettset als Ersatz hinzugefügt.', 'This solar panel cannot currently be ordered individually online. No bundle will be added as a substitute.') : this.text('Individuelle Anzahl: Preis und Anschlussplanung auf Anfrage.', 'Custom quantity: pricing and connection planning on request.');
        }
      }
      const comparison = engine.upgradeComparison(this.data, this.selection);
      const comparisonNode = this.groups.find(group => group.id === 'capacity').node.querySelector('.wvc__upgrade-comparison');
      comparisonNode.hidden = true;
      comparisonNode.textContent = comparison ? this.text('Zusätzlicher Speicher: ', 'Additional storage: ') + this.price(comparison.extra) + this.text(' insgesamt · Ø ', ' total · avg. ') + this.price(comparison.perBattery) + this.text(' je B500. Gegenüber ', ' per B500. Compared with ') + comparison.baselineCapacity + this.text(' bei gleicher Modulauswahl.', ' with the same panels.') : '';
      for (const output of this.root.querySelectorAll('[data-wvc-price]')) output.textContent = quote.total === null ? this.text('Auf Anfrage', 'On request') : this.price(quote.total);
      const offerNote = this.find('offer-note');
      if (offerNote) {
        offerNote.hidden = quote.discount === 0;
        offerNote.querySelector('strong').textContent = this.text('B500-Mengenrabatt: −', 'B500 quantity discount: −') + this.price(quote.discount);
      }
      const referenceTotal = this.result.total === null ? null : engine.referenceTotal(this.result.items);
      for (const output of this.root.querySelectorAll('[data-wvc-compare]')) {
        output.hidden = referenceTotal === null;
        output.textContent = referenceTotal === null ? '' : this.price(referenceTotal);
      }
      this.find('availability').textContent = this.result.requiresReview ? this.text('Zubehör-Kompatibilität noch nicht bestätigt. Bitte lass die Auswahl vor der Bestellung prüfen.', 'Accessory compatibility is not yet confirmed. Ask us to check your selection before ordering.') : this.result.valid ? '' : this.text('Diese Zusammenstellung ist aktuell nicht bestellbar. Bitte passe die Auswahl an oder frage uns.', 'This configuration cannot currently be ordered. Adjust your selection or contact us.');
      this.find('price-label').textContent = quote.discount ? this.text('Voraussichtlicher Gesamtpreis mit Rabatt', 'Estimated total with discount') : this.text('Gesamtpreis', 'Total');
      const stickyLabel = this.find('sticky-offer-label');
      if (stickyLabel) {
        stickyLabel.hidden = quote.discount === 0;
        stickyLabel.textContent = this.text('Inkl. B500-Rabatt: −', 'Incl. B500 discount: −') + this.price(quote.discount);
      }
      this.find('review-link').hidden = !this.result.requiresReview && !this.result.advisories?.length;
      this.find('review-link').textContent = quantityReview ? this.text('Individuelle Anzahl besprechen ↗', 'Discuss a custom quantity ↗') : this.text('Zubehör-Auswahl prüfen lassen ↗', 'Ask us to check these accessories ↗');
      this.find('scope').textContent = this.en ? '' : this.result.configuration?.note || '';
      this.find('remove-review').hidden = unverified.length === 0;
      this.root.querySelector('.wvc__purchase-help').hidden = this.find('remove-review').hidden && this.find('review-link').hidden;
      this.find('share-status').textContent = '';
      this.find('remove-review').disabled = this.busy;
      const blockingExtras = unverified.filter(item => item.option.independentPurchase !== true);
      if (blockingExtras.length && !quantityReview && this.result.reason === 'review') {
        const names = blockingExtras.map(item => {
          const group = this.groups.find(group => group.id === item.group);
          return group.title + ' (' + group.options.find(option => option.id === item.option.id).label + ')';
        });
        this.find('availability').textContent = this.text('Noch nicht für diese Zusammenstellung bestätigt: ', 'Not yet confirmed for this configuration: ') + names.join(', ') + this.text('. Entferne diese Extras oder lass sie vor der Bestellung prüfen.', '. Remove these extras or ask us to check them before ordering.');
      }
      if (this.result.advisories?.length && !quantityReview) {
        const names = this.result.advisories.map(item => this.groups.find(group => group.id === item.group).title);
        const advisory = names.join(', ') + this.text(': als separate Artikel im Warenkorb. Die Auswahl bestätigt keine Kompatibilität. Prüfe vor dem Kauf Maße, Befestigung und Steckverbindungen oder lass Dich beraten.', ': added as separate cart items. This selection does not confirm compatibility. Check dimensions, mounting and connectors before buying, or ask us for advice.');
        this.find('availability').textContent = [this.find('availability').textContent, advisory].filter(Boolean).join(' ');
      }
      if (quantityReview) {
        this.find('availability').textContent = this.result.reason === 'panel-quantity-invalid' ? this.text('Bitte gib eine ganze Modulanzahl von 0 bis 20 ein.', 'Enter a whole panel quantity from 0 to 20.') : this.text('Für diese Modulanzahl ist noch kein Preis hinterlegt. Bitte ändere die Auswahl.', 'This panel quantity has no confirmed price yet. Please change your selection.');
        this.find('price-label').textContent = this.text('Gesamtpreis', 'Total');
        this.find('scope').textContent = this.text('Die Abbildung zeigt Deine gewünschte Zusammenstellung. Preis und Lieferumfang sind noch nicht bestätigt.', 'The illustration shows your requested configuration. Pricing and included items are not yet confirmed.');
      }
      if (this.result.reason === 'panel-unavailable') this.find('availability').textContent = this.text('Das gewählte Solarmodul ist derzeit nicht einzeln online bestellbar. Wähle „Ohne Solarmodule“ oder frage uns. Es wird kein Komplettset hinzugefügt.', 'The selected solar panel cannot currently be ordered individually online. Choose “Without solar panels” or contact us. No bundle will be added.');
      if (this.result.reason === 'cable-quantity-invalid') this.find('availability').textContent = this.text('Bitte prüfe die Anzahl der Kabelpaare. Erlaubt sind ganze Zahlen ab 1.', 'Check the number of cable pairs. Enter a whole number of 1 or more.');
      if (main) {
        this.find('title').textContent = this.result.configuration.modelLabel;
        this.find('sticky-title').textContent = this.result.configuration.modelLabel;
        const extras = this.data.accessories.filter(group => group.options.some(option => option.id === this.selection[group.id] && option.product)).length;
        const summary = this.result.configuration.capacityLabel + ' · ' + (this.result.reason === 'panel-quantity-invalid' ? this.text('Anzahl prüfen', 'Check quantity') : this.result.configuration.panelsLabel) + (extras ? ' · ' + extras + this.text(' Zubehörposition', ' accessory item') + (extras > 1 ? this.text('en', 's') : '') : '');
        this.find('sticky-selection').textContent = summary;
        this.find('sticky-selection').title = summary;
        this.find('original').href = this.data.root.replace(/\/$/, '') + '/products/' + encodeURIComponent(main.handle) + (main.detailsView ? '?view=' + encodeURIComponent(main.detailsView) : '');
        const stickyImage = this.find('sticky-image');
        const stickyArt = this.find('sticky-art');
        stickyImage.src = main.summary_image || this.productImage(main);
        stickyImage.style.cssText = '';
        stickyArt.style.cssText = '';
        stickyArt.classList.toggle('is-framed', Boolean(main.summary_image && main.summary_frame));
        if (main.summary_image && main.summary_frame) this.frameSprite(stickyArt, main.summary_frame);
      }
      this.find('breakdown').replaceChildren();
      this.find('included').replaceChildren();
      this.find('included').hidden = !quantityReview && this.result.reason !== 'panel-unavailable';
      this.root.querySelector('.wvc__scope-details').hidden = !this.find('scope').textContent;
      if (this.result.reason === 'panel-unavailable') {
        for (const entry of this.result.configuration.items) {
          const product = this.data.products[entry.product];
          const name = entry.role === 'panels' ? this.result.configuration.panelsLabel + this.text(' – nicht einzeln bestellbar', ' – not available individually') : entry.quantity + ' × ' + (product ? this.productName(product) : this.text('Artikel nicht verfügbar', 'Item unavailable'));
          this.find('included').append(element('li', '', name));
        }
      }
      if (quantityReview) this.find('included').append(element('li', '', this.text('Lieferumfang wird mit dem Angebot bestätigt.', 'Included items will be confirmed with the quote.')));
      for (const item of this.result.items) {
        const row = element('li', 'wvc__selection-item');
        row.dataset.variantId = item.variant.id;
        row.dataset.quantity = item.quantity;
        const thumbnail = element('div', 'wvc__selection-image');
        const summarySource = item.variant.title === 'Default Title' ? item.product.summary_image : '';
        const source = summarySource || item.variant.featured_image?.src || this.productImage(item.product);
        const placeholder = element('small', '', this.text('Kein Bild', 'No image'));
        placeholder.hidden = Boolean(source);
        thumbnail.append(placeholder);
        if (source) {
          const image = this.image(source, '', 240);
          image.addEventListener('error', () => {
            image.hidden = true;
            if (image.parentElement !== thumbnail) image.parentElement.hidden = true;
            placeholder.hidden = false;
          }, { once: true });
          if (summarySource && item.product.summary_frame) {
            const sprite = element('div', 'wvc__selection-sprite');
            sprite.append(image);
            this.frameSprite(sprite, item.product.summary_frame);
            thumbnail.append(sprite);
          } else thumbnail.append(image);
        }
        const description = element('span', 'wvc__selection-description');
        const quantity = element('span', 'wvc__selection-quantity', item.quantity + ' × ');
        quantity.setAttribute('aria-label', this.text('Anzahl: ', 'Quantity: ') + item.quantity);
        description.append(quantity, element('span', 'wvc__selection-name', this.itemName(item)));
        const prices = element('div', 'wvc__selection-prices');
        const reference = engine.referencePrice(item.variant, item.quantity);
        if (reference !== null) {
          const previous = element('s', 'wvc__compare-price', this.price(reference));
          previous.setAttribute('aria-label', this.text('Vergleichspreis: ', 'Comparison price: ') + this.price(reference));
          prices.append(previous);
        }
        prices.append(element('strong', 'wvc__selection-price', this.price(item.variant.price * item.quantity)));
        row.append(thumbnail, description, prices);
        this.find('breakdown').append(row);
      }
      if (quote.discount) {
        const row = element('li', 'wvc__offer-line');
        row.append(element('span', '', this.text('B500-Mengenrabatt · für diese Auswahl', 'B500 quantity discount · for this selection')), element('strong', '', '−' + this.price(quote.discount)));
        this.find('breakdown').append(row);
      }
      this.gallery();
      this.buttons();
      this.root.dispatchEvent(new CustomEvent('wv:configuration-change', { bubbles: true, detail: { model: this.selection.model } }));
    }
    buttons() {
      const block = engine.purchaseBlock(this);
      const guard = this.find('purchase-guard');
      const message = this.find('guard-message');
      const review = this.find('review-selection');
      let explanation = '';
      if (block === 'acknowledge') explanation = this.text('Einige Angaben im Link konnten nicht übernommen werden. Prüfe Artikel, Mengen und Gesamtpreis und bestätige die Auswahl.', 'Some options in the link could not be restored. Check the items, quantities and total, then confirm your selection.');
      else if (block === 'cart') explanation = this.text('Eine frühere Übertragung konnte nicht eindeutig bestätigt werden. Prüfe zuerst den Warenkorb, damit keine Artikel doppelt hinzugefügt werden.', 'A previous transfer could not be confirmed. Check your cart first to avoid adding items twice.');
      else if (block === 'invalid') explanation = this.find('availability').textContent.trim() || this.text('Diese Zusammenstellung kann derzeit nicht hinzugefügt werden. Prüfe die gewählten Artikel und Mengen.', 'This configuration cannot currently be added. Check the selected items and quantities.');
      message.textContent = explanation;
      guard.hidden = !explanation;
      review.hidden = !explanation;
      review.textContent = block === 'cart' ? this.text('Warenkorb prüfen', 'Review cart') : block === 'acknowledge' ? this.text('Auswahl prüfen und bestätigen', 'Review and confirm selection') : this.text('Auswahl prüfen', 'Review selection');
      for (const button of this.root.querySelectorAll('[data-wvc-add], [data-wvc-buy]')) {
        button.disabled = Boolean(block);
        if (explanation) button.setAttribute('aria-describedby', message.id);
        else button.removeAttribute('aria-describedby');
        button.textContent = this.busy ? this.text('Wird geprüft …', 'Checking…') : button.hasAttribute('data-wvc-buy') ? this.text('Jetzt kaufen', 'Buy now') : this.text('In den Warenkorb', 'Add to cart');
      }
      this.find('acknowledge').hidden = block !== 'acknowledge';
      this.find('recheck-cart').hidden = block !== 'cart';
      this.find('remove-review').disabled = this.busy;
      for (const input of this.root.querySelectorAll('input[type=radio]')) if (this.busy) input.disabled = true;
      if (this.busy) for (const input of this.root.querySelectorAll('[data-cable-quantity], [data-cable-step]')) input.disabled = true;
    }
    gallery() {
      const main = this.mainProduct();
      if (!main) return;
      const thumbs = this.find('thumbs');
      thumbs.replaceChildren();
      const choose = (button, callback) => {
        thumbs.querySelectorAll('button').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        callback();
      };
      const compositeButton = element('button');
      compositeButton.type = 'button';
      compositeButton.setAttribute('aria-label', this.text('Aktuelle Zusammenstellung', 'Current configuration'));
      compositeButton.append(element('span', 'wvc__set-thumb', this.text('Deine Auswahl', 'Your selection')));
      compositeButton.addEventListener('click', () => choose(compositeButton, () => this.showComposition()));
      thumbs.append(compositeButton);
      const images = this.result.reason?.startsWith('panel-quantity') ? [] : main.images.slice(0, 5).map(image => typeof image === 'string' ? image : image.src);
      for (const button of this.root.querySelectorAll('[data-wvc-previous], [data-wvc-next]')) button.disabled = images.length === 0;
      images.forEach((source, index) => {
        const button = element('button');
        button.type = 'button';
        button.setAttribute('aria-label', this.text('Produktbild ', 'Product image ') + (index + 1));
        button.append(this.image(source, '', 80));
        button.addEventListener('click', () => choose(button, () => this.showImage(source, this.productName(main))));
        thumbs.append(button);
      });
      choose(compositeButton, () => this.showComposition());
    }
    galleryStep(direction) {
      const buttons = [...this.find('thumbs').querySelectorAll('button')];
      if (buttons.length < 2) return;
      const current = buttons.findIndex(button => button.getAttribute('aria-pressed') === 'true');
      buttons[(current + direction + buttons.length) % buttons.length].click();
    }
    showComposition() {
      this.mediaVersion += 1;
      this.root.querySelector('.wvc__stage').setAttribute('aria-busy', 'false');
      const composition = this.find('composition');
      composition.dataset.model = this.selection.model;
      const layers = engine.scene(this.data, this.selection);
      const groupFor = { main: 'model', battery: 'capacity', panels: 'panels', mount: 'mount', meter: 'meter', ac_cable: 'ac_cable', dc_cable: 'dc_cable' };
      const names = { main: this.result.configuration.modelLabel.replace('SunEnergyXT ', ''), battery: 'B500', panels: this.text('Solarmodule', 'Solar panels'), mount: this.text('Halterung', 'Mounting'), meter: 'EcoTracker', ac_cable: this.text('AC-Kabel', 'AC cable'), dc_cable: this.text('DC-Kabel', 'DC cable') };
      for (const [slot, layer] of Object.entries(layers)) {
        let component = composition.querySelector('[data-slot="' + slot + '"]');
        if (!component) {
          component = element('div', 'wvc__component');
          component.dataset.slot = slot;
          const images = element('div', 'wvc__component-images');
          for (let index = 0; index < 1; index += 1) {
            const sprite = element('div', 'wvc__sprite');
            sprite.dataset.index = index;
            sprite.style.setProperty('--sprite-index', index);
            const image = element('img');
            image.width = 500;
            image.height = 500;
            image.draggable = false;
            image.dataset.index = index;
            image.addEventListener('error', () => {
              image.hidden = true;
              sprite.hidden = true;
              image.dataset.failedSource = image.getAttribute('src');
              component.dataset.imageError = 'true';
            });
            sprite.append(image);
            images.append(sprite);
          }
          const copy = element('div', 'wvc__component-copy');
          copy.append(element('strong'), element('span', 'wvc__component-meta'));
          const shortcut = element('button', 'wvc__component-action');
          shortcut.type = 'button';
          shortcut.dataset.focusGroup = groupFor[slot];
          component.append(images, element('span', 'wvc__component-count'), copy, shortcut);
          composition.append(component);
        }
        component.dataset.active = String(Boolean(layer));
        const optionGroup = this.groups.find(group => group.id === groupFor[slot]);
        const availableGroup = optionGroup && !optionGroup.node.hidden;
        const unavailableGroup = optionGroup?.node.dataset.unavailable === 'true';
        component.dataset.unavailable = String(unavailableGroup);
        component.style.visibility = availableGroup ? '' : 'hidden';
        component.setAttribute('aria-hidden', String(!availableGroup));
        component.dataset.count = layer?.quantity || 0;
        const shortcut = component.querySelector('button');
        shortcut.disabled = !availableGroup || unavailableGroup || this.busy;
        shortcut.setAttribute('aria-label', names[slot] + this.text(' auswählen', ' options'));
        for (const image of component.querySelectorAll('img')) {
          const source = this.imageSource(slot === 'mount' && layer ? this.data.mountExample || layer.source : layer?.source, slot === 'main' ? 700 : slot === 'panels' ? 500 : 400);
          const visible = Boolean(source) && image.dataset.failedSource !== source;
          image.hidden = !visible;
          image.parentElement.hidden = !visible;
          this.frameSprite(image.parentElement, slot === 'mount' && this.data.mountExample ? [1440, 736, 600, 210, 830, 450] : this.frameFor(slot));
          const width = slot === 'main' ? 62 : slot === 'panels' ? 8 : slot === 'meter' ? 10 : slot === 'dc_cable' ? 11 : 14;
          component.style.setProperty('--component-image-width', width + 'cqw');
          if (visible && image.getAttribute('src') !== source) {
            delete component.dataset.imageError;
            image.src = source;
          }
          image.alt = visible && image.dataset.index === '0' ? slot === 'mount' ? this.text('Beispiel einer Balkonmontage, nicht die gewählte Halterung', 'Example balcony installation, not the selected bracket') : layer.label : '';
        }
        const count = component.querySelector('.wvc__component-count');
        count.hidden = !layer;
        count.textContent = layer ? layer.quantity + '×' : '';
        if (slot === 'mount' && layer) count.textContent = this.text('1 Set', '1 set');
        if (slot === 'dc_cable' && layer) count.textContent = this.text('1 Paar', '1 pair');
        component.querySelector('strong').textContent = names[slot];
        const meta = component.querySelector('.wvc__component-meta');
        meta.textContent = !layer ? this.text('+ Auswählen', '+ Select') : slot === 'panels' ? this.result.configuration.panelWp + this.text(' Wp je Modul', ' Wp per panel') : slot === 'mount' ? this.text('Montagebeispiel', 'Installation example') : slot === 'dc_cable' ? this.groups.find(group => group.id === 'dc_cable').options.find(option => option.id === this.selection.dc_cable)?.label || '' : slot === 'main' ? this.text('Hauptgerät', 'Main unit') : slot === 'battery' ? this.text('Erweiterung', 'Expansion') : '';
        component.title = layer?.label || '';
        if (layer && (!layer.source || component.dataset.imageError)) meta.textContent = this.text('Abbildung nicht verfügbar', 'Image unavailable');
        if (unavailableGroup) meta.textContent = this.text('Nicht auswählbar', 'Not selectable');
      }
      this.find('hero').hidden = true;
      composition.hidden = false;
      const parts = [this.result.configuration.modelLabel, this.result.configuration.capacityLabel, layers.panels?.label || ''].filter(Boolean);
      this.find('caption').textContent = parts.join(' · ') + this.text('. Produktdarstellung, nicht maßstabsgetreu.', '. Product illustration, not to scale.');
      if (layers.mount) this.find('caption').textContent += this.text(' Halterung: Beispielabbildung.', ' Mount: illustrative image.');
    }
    showImage(source, alt) {
      const version = ++this.mediaVersion;
      this.root.querySelector('.wvc__stage').setAttribute('aria-busy', 'true');
      this.find('caption').textContent = this.text('Produktbild wird aktualisiert …', 'Updating product image…');
      const loader = new Image();
      loader.onload = () => {
        if (version !== this.mediaVersion || !this.root.isConnected) return;
        const hero = this.find('hero');
        hero.src = source;
        hero.alt = alt;
        hero.hidden = false;
        this.find('composition').hidden = true;
        this.root.querySelector('.wvc__stage').setAttribute('aria-busy', 'false');
        this.find('caption').textContent = this.text('Produktabbildung. Verbindlich ist der aufgeführte Lieferumfang.', 'Product image. Refer to the listed scope of delivery.');
      };
      loader.onerror = () => {
        if (version === this.mediaVersion) {
          this.find('hero').hidden = true;
          this.find('composition').hidden = true;
          this.root.querySelector('.wvc__stage').setAttribute('aria-busy', 'false');
          this.find('caption').textContent = this.text('Bild konnte nicht geladen werden. Deine Auswahl bleibt erhalten.', 'The image could not be loaded. Your selection is retained.');
        }
      };
      loader.src = source;
    }
    writeURL() {
      const url = new URL(location.href);
      if (this.selection.panelQuantity === undefined) url.searchParams.delete('cfg_panelQuantity');
      if (this.selection.dcQuantity === undefined) url.searchParams.delete('cfg_dcQuantity');
      for (const [key, value] of Object.entries(this.selection)) url.searchParams.set('cfg_' + key, value);
      url.searchParams.set('cfg_v', '1');
      if (this.needsAcknowledgment) url.searchParams.set('cfg_review', '1');
      else url.searchParams.delete('cfg_review');
      history.replaceState({}, '', url);
    }
    async share() {
      this.writeURL();
      try {
        await navigator.clipboard.writeText(location.href);
        this.find('share-status').textContent = this.text('Link zur Konfiguration kopiert.', 'Configuration link copied.');
      } catch {
        const input = element('input', 'wvc__share-input');
        input.type = 'url';
        input.readOnly = true;
        input.value = location.href;
        input.setAttribute('aria-label', this.text('Konfigurationslink', 'Configuration link'));
        this.dialog(this.text('Konfiguration teilen', 'Share configuration'), input);
        input.select();
      }
    }
    dialog(title, content) {
      this.find('dialog-title').textContent = title;
      this.find('dialog-body').replaceChildren(content);
      this.find('dialog').showModal();
    }
    async add(destination = 'cart') {
      if (engine.purchaseBlock(this)) return;
      this.busy = true;
      this.buttons();
      const previousTotal = this.result.total;
      let attempt = null;
      let submitted = null;
      const route = this.data.root.replace(/\/$/, '');
      try {
        const handles = [...new Set(this.result.items.map(item => item.product.handle))];
        const fresh = await Promise.all(handles.map(handle => request(route + '/products/' + encodeURIComponent(handle) + '.js')));
        for (const product of fresh) this.data.products[product.handle] = product;
        this.result = engine.resolve(this.data, this.selection);
        if (!this.result.valid) { this.status(this.text('Die Verfügbarkeit hat sich geändert. Bitte prüfe Deine Auswahl.', 'Availability has changed. Please check your selection.')); return; }
        if (this.result.total !== previousTotal) { this.status(this.text('Der Preis wurde aktualisiert. Bitte prüfe den neuen Gesamtpreis und bestätige erneut.', 'The price has changed. Check the new total and confirm again.')); return; }
        attempt = 'wv-' + (crypto.randomUUID ? crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2));
        submitted = engine.cartItems(this.result, attempt);
        sessionStorage.setItem('wv-configurator-pending', JSON.stringify({ version: 1, attempt, items: submitted, currency: this.data.currency, prices: this.result.items.map(item => ({ id: item.variant.id, price: item.variant.price })) }));
        await request(route + '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: submitted }) });
        const cart = await request(route + '/cart.js');
        if (!this.confirmed(cart, submitted, attempt)) { this.cartIssue = true; throw new Error('Cart needs review'); }
        this.success(cart, submitted, attempt, destination);
      } catch (error) {
        let rejectedWithoutChanges = false;
        if (attempt) {
          try {
            const cart = await request(route + '/cart.js');
            if (this.confirmed(cart, submitted, attempt)) { this.success(cart, submitted, attempt, destination); return; }
            if (error.status === 422 && !cart.items.some(item => item.properties?._wv_configuration === attempt)) {
              sessionStorage.removeItem('wv-configurator-pending');
              this.cartIssue = false;
              rejectedWithoutChanges = true;
            } else this.cartIssue = true;
          } catch { this.cartIssue = true; }
        }
        this.status(this.cartIssue ? this.text('Bitte prüfe den Warenkorb, bevor Du erneut hinzufügst. Die Übertragung konnte nicht vollständig bestätigt werden.', 'Check your cart before adding again. We could not fully confirm the transfer.') : rejectedWithoutChanges ? this.text('Die Auswahl wurde nicht hinzugefügt. Bitte prüfe die gewünschte Menge und Verfügbarkeit und versuche es erneut.', 'Your selection was not added. Check the requested quantity and availability, then try again.') : error.code === 'RATE_LIMITED' ? this.text('Der Shop begrenzt gerade weitere Anfragen. Bitte warte einen Moment und versuche es erneut. Deine Auswahl bleibt erhalten.', 'The store is temporarily limiting requests. Please wait a moment and try again. Your selection is retained.') : this.text('Die Auswahl konnte nicht hinzugefügt werden. Bitte prüfe Deine Verbindung und versuche es erneut.', 'Your selection could not be added. Check your connection and try again.'));
        this.find('cart-link').hidden = false;
      } finally {
        this.busy = false;
        this.render();
      }
    }
    confirmed(cart, items, attempt) {
      return engine.confirmedCart(cart, items, attempt);
    }
    async reconcileCart() {
      if (this.busy || this.checkoutPending || !this.cartIssue) return;
      const receipt = engine.pendingTransfer(sessionStorage.getItem('wv-configurator-pending'));
      if (!receipt?.attempt) return;
      this.busy = true;
      this.buttons();
      try {
        const cart = await request(this.data.root.replace(/\/$/, '') + '/cart.js');
        const expected = receipt.items || (this.result.valid ? engine.cartItems(this.result, receipt.attempt) : []);
        const prices = receipt.prices || this.result.items.map(item => ({ id: item.variant.id, price: item.variant.price }));
        const lines = cart.items.filter(item => item.properties?._wv_configuration === receipt.attempt);
        const quoteMatches = cart.currency === (receipt.currency || this.data.currency) && lines.every(line => prices.some(item => String(item.id) === String(line.variant_id) && item.price === line.original_price));
        if (this.confirmed(cart, expected, receipt.attempt) && quoteMatches) {
          sessionStorage.removeItem('wv-configurator-pending');
          this.cartIssue = false;
          this.status(this.text('Die frühere Zusammenstellung ist bereits im Warenkorb. Es wurden keine weiteren Artikel hinzugefügt.', 'Your previous configuration is already in the cart. No additional items were added.'));
          this.notifyCart(cart);
        } else {
          this.status(this.text('Die frühere Übertragung ist noch nicht eindeutig zugeordnet. Prüfe die Artikel im Warenkorb, bevor Du erneut bestellst.', 'The previous transfer could not be matched completely. Review the items in your cart before ordering again.'));
        }
      } catch {
        this.status(this.text('Der Warenkorb konnte nicht geprüft werden. Bitte versuche die Prüfung erneut. Es wurden keine Artikel hinzugefügt.', 'We could not check your cart. Please try the check again. No items were added.'));
      } finally {
        this.busy = false;
        this.find('cart-link').hidden = false;
        this.render();
      }
    }
    notifyCart(cart, open = false) {
      const update = () => {
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        if (open) document.dispatchEvent(new CustomEvent('cart:open'));
      };
      update();
      if (!window.theme?.sections) document.addEventListener('page:loaded', update, { once: true, signal: this.controllers?.signal });
    }
    success(cart, submitted, attempt, destination = 'cart') {
      const lines = cart.items.filter(item => item.properties?._wv_configuration === attempt);
      const quoteMatches = this.confirmed(cart, submitted, attempt) && cart.currency === this.data.currency && lines.every(line => this.result.items.some(expected => Number(line.variant_id) === Number(expected.variant.id) && line.original_price === expected.variant.price));
      if (quoteMatches) sessionStorage.removeItem('wv-configurator-pending');
      this.cartIssue = !quoteMatches;
      this.status(quoteMatches ? this.text('Deine Zusammenstellung wurde zum Warenkorb hinzugefügt.', 'Your configuration has been added to the cart.') : this.text('Die Artikel wurden hinzugefügt, aber Preis oder Währung haben sich geändert. Bitte prüfe den Warenkorb vor dem Kauf.', 'The items were added, but the price or currency has changed. Please review your cart before purchasing.'));
      this.find('cart-link').hidden = false;
      if (destination === 'checkout' && quoteMatches) {
        this.checkoutPending = true;
        window.location.assign(this.data.root.replace(/\/$/, '') + '/checkout');
        return;
      }
      this.notifyCart(cart, true);
    }
    destroy() { this.controllers.abort(); this.observer?.disconnect(); this.stickySizeObserver?.disconnect(); document.body.style.removeProperty('--wvc-sticky-height'); this.mediaVersion += 1; }
  }
  function init(container = document) {
    container.querySelectorAll('[data-wv-500-series]').forEach(root => {
      if (instances.has(root)) return;
      try { instances.set(root, new Configurator(root)); }
      catch (error) {
        const status = root.querySelector('[data-wvc-status]');
        if (status) status.textContent = document.documentElement.lang.startsWith('en') ? 'The configurator is unavailable. Please open the product details or contact our team.' : 'Der Konfigurator ist nicht verfügbar. Bitte öffne die Produktdetails oder kontaktiere unser Team.';
      }
    });
  }
  window.WV500Series = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-wv-500-series]').forEach(root => { instances.get(root)?.destroy(); instances.delete(root); }));
  init();
})();
